import type Palette from 'iwanthue/palette'
import { atom } from 'jotai'
import { type DependencyWithAll } from '#app/data/dependency.server.js'
import { type ElementWithAll } from '#app/data/element.server.js'
import { type BackgroundImageMetadata } from '#app/data/models/background-image.model.js'
import {
  type EditorAction,
  type EditorSearch,
} from '#app/data/models/editor.model.js'
import { type PatternGraphSettings } from '#app/data/models/graph.model.js'
import {
  type PatternWithAll,
  type UserPatternSettings,
} from '#app/data/pattern.server.js'

// Main entities
export const patternAtom = atom<PatternWithAll | null>(null)
export const elementsAtom = atom<ElementWithAll[] | null>(null)
export const dependenciesAtom = atom<DependencyWithAll[] | null>(null)

// Search-related state
export const searchedElementIdsAtom = atom<string[] | null>(null)
export const searchedDependencyIdsAtom = atom<string[] | null>(null)
export const editorSearchAtom = atom<EditorSearch | null>(null)

// Selection state
export const selectedElementIdsAtom = atom<string[]>([])
export const selectedDependencyIdAtom = atom<string | null>(null)
export const selectedElementIdsForPlacingAtom = atom<string[]>([])
export const selectedEditorActionAtom = atom<EditorAction | null>(null)
export const elementGroupColorPaletteAtom = atom<Palette<string> | null>(null)
export const elementGroupLimitExceededAtom = atom<{
  exceeded: boolean
  total: number
} | null>(null)
export const userPatternSettingsAtom = atom<UserPatternSettings | null>(null)
export const patternSettingsAtom = atom<PatternGraphSettings | null>(null)

// Background image
export const backgroundImageAtom = atom<BackgroundImageMetadata | null>(null)
export const backgroundImageBlobUrlAtom = atom<string | null>(null)

// Cursor state
export const cursorTypeAtom = atom<string>('grab')
export const cursorTypeForNodeAtom = atom<string>('pointer')
export const cursorTypeForEdgeAtom = atom<string>('pointer')

// Pending and staged elements
export const pendingElementIdsAtom = atom<string[]>([])
export const stagedElementIdsAtom = atom<string[]>([])

// Bookmark state
export const bookmarkedElementIdsAtom = atom<string[]>([])
export const isBookmarksOpenAtom = atom<boolean>(false)

// Element edit state
export const isElementEditOpenAtom = atom<boolean>(false)
export const isElementListOpenAtom = atom<boolean>(false)
export const isLegendListOpenAtom = atom<boolean>(false)
export const isDependencyEditOpenAtom = atom<boolean>(false)
export const isMatrixViewOpenAtom = atom<boolean>(false)

// Delete-related state
export const deletableElementIdsAtom = atom<string[]>([])
export const isElementDeleteDialogOpenAtom = atom<boolean>(false)

// Pattern access level (reservation-based, not project role)
export type PatternAccessLevel = 'editor' | 'reader'
export const patternAccessLevelAtom = atom<PatternAccessLevel>('editor')
export const isPatternReadonlyAtom = atom(
  get => get(patternAccessLevelAtom) === 'reader',
)

// Reservation holder info (for readonly banner)
export const userWithReservationAtom = atom<{
  id: string
  name: string | null
} | null>(null)

// Hotkeys
export const modifierKeyHeldAtom = atom<string | null>(null)
export const disablePatternHotkeysAtom = atom<boolean>(false)
